import {
  booleanAttribute,
  ContentChildren,
  EventEmitter,
  forwardRef,
  InjectionToken,
  Input,
  Output,
  QueryList,
  Component,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { v4 as uuidv4 } from 'uuid';
import { RadioButtonDirective } from '@app/shared/components/radio-buttons/radio-button.directive';

export const RADIO_GROUP_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => RadioGroupComponent),
  multi: true,
};

export const RADIO_GROUP = new InjectionToken<RadioGroupComponent<any>>('RadioGroupDirective');

@Component({
  selector: 'app-radio-group',
  templateUrl: './radio-group-component.html',
  styleUrl: './radio-group.component.scss',
  providers: [RADIO_GROUP_CONTROL_VALUE_ACCESSOR, { provide: RADIO_GROUP, useExisting: RadioGroupComponent }],
  host: {
    role: 'radiogroup',
  },
})
export class RadioGroupComponent<T> implements ControlValueAccessor {
  private _value: T | null = null;
  private _name = uuidv4();
  private _selected: RadioButtonDirective<T> | null = null;
  private _disabled: boolean = false;
  private _required: boolean = false;

  onChange: (value: T | null) => void = () => {};

  onTouched: () => void = () => {};

  @ContentChildren(forwardRef(() => RadioButtonDirective), { descendants: true })
  private radios!: QueryList<RadioButtonDirective<T>>;

  @Input()
  get value(): any {
    return this._value;
  }
  set value(newValue: any) {
    if (this._value !== newValue) {
      this._value = newValue;
      this.updateSelectedRadioFromValue();
    }
  }

  @Input()
  get name(): string {
    return this._name;
  }
  set name(value: string) {
    this._name = value;
    this.updateRadioButtonNames();
  }

  @Input()
  get selected() {
    return this._selected;
  }
  set selected(selected: RadioButtonDirective<T> | null) {
    if (this._selected !== selected) {
      this._selected = selected;
      this.value = selected ? selected.value : null;
    }
  }

  @Input({ transform: booleanAttribute })
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: boolean) {
    this._disabled = value;
  }

  @Input({ transform: booleanAttribute })
  get required(): boolean {
    return this._required;
  }
  set required(value: boolean) {
    this._required = value;
  }

  @Output() readonly change: EventEmitter<T> = new EventEmitter<T>();

  /**
   * Sets the model value. Implemented as part of ControlValueAccessor.
   * @param value
   */
  writeValue(value: any) {
    this.value = value;
  }

  /**
   * Registers a callback to be triggered when the model value changes.
   * Implemented as part of ControlValueAccessor.
   * @param fn Callback to be registered.
   */
  registerOnChange(fn: (value: any) => void) {
    this.onChange = fn;
  }

  /**
   * Registers a callback to be triggered when the control is touched.
   * Implemented as part of ControlValueAccessor.
   * @param fn Callback to be registered.
   */
  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  /**
   * Sets the disabled state of the control. Implemented as a part of ControlValueAccessor.
   * @param isDisabled Whether the control should be disabled.
   */
  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  emitChangeEvent() {
    if (this.selected) {
      this.change.emit(this.value);
    }
  }

  private updateRadioButtonNames() {
    if (this.radios) {
      this.radios.forEach((radio) => {
        radio.name = this.name;
      });
    }
  }

  private updateSelectedRadioFromValue() {
    if (!this.radios) return;

    this._selected = null;
    this.radios.forEach((radio) => {
      radio.checked = this.value === radio.value;
      if (radio.checked) {
        this._selected = radio;
      }
    });
  }
}
